import { useEffect, useMemo, useRef, useCallback } from "react";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { useShallow } from "zustand/react/shallow";
import { useProducts } from "@/components/product-step/useProducts";
import { useClinics } from "@/hooks/useClinics";
import { useClinicProductAvailability } from "@/hooks/useClinicProductAvailability";
import { useClinicExternalSlots } from "@/hooks/useClinicExternalSlots";
import { useLanguage } from "@/hooks/booking/useLanguage";
import { coerceLanguageForMarket } from "@/config/market";
import type { TimeSlot } from "@/types/booking";

/**
 * Ensures a value is a proper Date object (handles string dates from serialization)
 */
function ensureDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Ensures TimeSlot dates are proper Date objects
 */
function ensureTimeSlotDates(slots: TimeSlot[]): TimeSlot[] {
  return slots
    .map((slot) => {
      const date = ensureDate(slot.date);
      if (!date) return null;
      return { ...slot, date };
    })
    .filter((s): s is TimeSlot => s !== null);
}

/**
 * Convenience hook for using the booking flow state machine in components
 *
 * This hook:
 * 1. Automatically syncs products, clinics, and language data with the store
 * 2. Returns the full store state and actions
 * 3. Provides type-safe access to all booking flow functionality
 *
 * Uses useShallow to only re-render when the specific state fields change,
 * rather than on every store update.
 */
export const useBookingFlow = () => {
  // Select only the state fields we need (shallow comparison prevents unnecessary re-renders)
  const state = useBookingFlowStore(
    useShallow((s) => ({
      currentStep: s.currentStep,
      stepHistory: s.stepHistory,
      viewMode: s.viewMode,
      selectedPackageType: s.selectedPackageType,
      selectedProducts: s.selectedProducts,
      selectedAddOns: s.selectedAddOns,
      configureProducts: s.configureProducts,
      selectedPackageId: s.selectedPackageId,
      selectedClinic: s.selectedClinic,
      selectedDate: s.selectedDate,
      selectedTimeSlots: s.selectedTimeSlots,
      secondSlotCalendarDate: s.secondSlotCalendarDate,
      bookingType: s.bookingType,
      formData: s.formData,
      bookingId: s.bookingId,
      isLoading: s.isLoading,
      isSubmitting: s.isSubmitting,
      isSubmitted: s.isSubmitted,
      language: s.language,
      market: s.market,
      _products: s._products,
      _clinics: s._clinics,
    })),
  );

  // Select actions individually (functions are stable references in Zustand)
  const actions = useBookingFlowStore(
    useShallow((s) => ({
      goToStep: s.goToStep,
      goBack: s.goBack,
      setViewMode: s.setViewMode,
      setSelectedPackageType: s.setSelectedPackageType,
      attachScanToMembership: s.attachScanToMembership,
      detachScanFromMembership: s.detachScanFromMembership,
      selectProducts: s.selectProducts,
      addProduct: s.addProduct,
      removeProduct: s.removeProduct,
      setSelectedPackageId: s.setSelectedPackageId,
      setConfigureProducts: s.setConfigureProducts,
      toggleAddOn: s.toggleAddOn,
      setSelectedAddOns: s.setSelectedAddOns,
      selectClinic: s.selectClinic,
      selectDate: s.selectDate,
      selectTimeSlot: s.selectTimeSlot,
      deleteTimeSlot: s.deleteTimeSlot,
      setSecondSlotCalendarDate: s.setSecondSlotCalendarDate,
      setBookingType: s.setBookingType,
      updateFormData: s.updateFormData,
      submitBooking: s.submitBooking,
      setSubmitting: s.setSubmitting,
      setSubmitted: s.setSubmitted,
      setBookingId: s.setBookingId,
      validateStep: s.validateStep,
      canProceedFromStep: s.canProceedFromStep,
      setLanguage: s.setLanguage,
      setMarket: s.setMarket,
      showToast: s.showToast,
      resetFlow: s.resetFlow,
      resetFromStep: s.resetFromStep,
      initializeFromUrl: s.initializeFromUrl,
      initializeTestModeNavigation: s.initializeTestModeNavigation,
    })),
  );

  // Get language from URL/localStorage (this reads ?locale=de correctly)
  const { language: urlLanguage } = useLanguage();

  // Get products and clinics data from existing hooks (market-aware pricing)
  const { data: productData } = useProducts(state.language, state.market);
  const { data: clinics } = useClinics();
  const { data: availabilityData } = useClinicProductAvailability();
  const {
    data: externalSlots,
    isLoading: isLoadingExternalSlots,
    isFetched: hasExternalSlotsFetched,
  } = useClinicExternalSlots(state.selectedClinic ?? undefined);

  // Get stable references to sync functions
  const setProducts = useBookingFlowStore((s) => s.setProducts);
  const setClinics = useBookingFlowStore((s) => s.setClinics);
  const setClinicProductAvailability = useBookingFlowStore(
    (s) => s.setClinicProductAvailability,
  );
  const setClinicExternalSlots = useBookingFlowStore(
    (s) => s.setClinicExternalSlots,
  );
  const setStoreLanguage = useBookingFlowStore((s) => s.setLanguage);

  // Sync language from URL/localStorage to store - ONLY on initial mount
  // This ensures ?locale=de parameter is respected when app loads
  useEffect(() => {
    // French is Switzerland-only: coerce against the (possibly persisted) market
    // so a DE-market reload never resyncs a stale French selection.
    const initialLanguage = coerceLanguageForMarket(
      useBookingFlowStore.getState().market,
      urlLanguage,
    );
    const storeLanguage = useBookingFlowStore.getState().language;

    if (initialLanguage && initialLanguage !== storeLanguage) {
      console.log(
        "[useBookingFlow] Initial language sync to store:",
        initialLanguage,
      );
      setStoreLanguage(initialLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps = run only once on mount

  // Sync products with store when they change
  useEffect(() => {
    if (productData?.products) {
      setProducts(productData.products);
    }
  }, [productData?.products, setProducts]);

  // Sync clinics with store when they change
  useEffect(() => {
    if (clinics) {
      setClinics(clinics);
    }
  }, [clinics, setClinics]);

  // Sync clinic product availability with store when it changes
  useEffect(() => {
    if (availabilityData) {
      setClinicProductAvailability(availabilityData);
    }
  }, [availabilityData, setClinicProductAvailability]);

  // Sync external slots with store when they're fetched for the selected clinic
  // This enables time slot validation when changing clinics
  // IMPORTANT: Only sync after fetch completes to avoid race condition during session restore
  useEffect(() => {
    if (hasExternalSlotsFetched && state.selectedClinic) {
      // externalSlots can be null (no clinic), undefined (loading), or [] (no slots in DB)
      // After fetch completes, we use the actual data (which may be empty array)
      setClinicExternalSlots(state.selectedClinic, externalSlots || []);
    }
  }, [
    hasExternalSlotsFetched,
    externalSlots,
    state.selectedClinic,
    setClinicExternalSlots,
  ]);

  // Ensure dates are proper Date objects (handles string dates from serialization)
  const selectedDate = useMemo(
    () => ensureDate(state.selectedDate),
    [state.selectedDate],
  );
  const selectedTimeSlots = useMemo(
    () => ensureTimeSlotDates(state.selectedTimeSlots),
    [state.selectedTimeSlots],
  );

  // Compute derived values — these use getState() to avoid subscribing to _products/_clinics
  const firstAvailableDate = useMemo(
    () => useBookingFlowStore.getState().getFirstAvailableDate(),
    // Re-compute when the inputs to getFirstAvailableDate change — incl. the
    // catalogs, so a session restored before useClinics/useProducts resolve
    // recomputes once the data lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state.selectedClinic,
      state.selectedProducts,
      state.selectedAddOns,
      state._clinics,
      state._products,
    ],
  );

  const defaultSecondSlotDate = useMemo(
    () => useBookingFlowStore.getState().getDefaultSecondSlotDate(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state.selectedTimeSlots,
      state.selectedClinic,
      state.selectedProducts,
      state.selectedAddOns,
      state._clinics,
      state._products,
    ],
  );

  const isDateValid = useMemo(
    () => useBookingFlowStore.getState().isDateValid(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state.selectedDate,
      state.selectedClinic,
      state.selectedProducts,
      state.selectedAddOns,
      state._clinics,
      state._products,
    ],
  );

  const selectedProductDetails = useMemo(
    () => useBookingFlowStore.getState().getSelectedProductDetails(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.selectedProducts, state._products],
  );

  const selectedAddOnDetails = useMemo(
    () => useBookingFlowStore.getState().getSelectedAddOnDetails(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.selectedAddOns, state._products],
  );

  const selectedClinicDetails = useMemo(
    () => useBookingFlowStore.getState().getSelectedClinicDetails(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.selectedClinic, state._clinics],
  );

  const canProceed = useMemo(
    () => useBookingFlowStore.getState().canProceedFromStep(state.currentStep),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state.currentStep,
      state.selectedProducts,
      state.selectedAddOns,
      state.selectedClinic,
      state.selectedDate,
      state.selectedTimeSlots,
      state.formData,
      state.bookingType,
    ],
  );

  const stepHistory = state.stepHistory;
  const canGoBack = useMemo(
    () => useBookingFlowStore.getState().canGoBack(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stepHistory],
  );

  // Return memoized result — only changes when individual values change
  return useMemo(
    () => ({
      // ===== STATE =====
      currentStep: state.currentStep,
      stepHistory: state.stepHistory,
      viewMode: state.viewMode,
      selectedPackageType: state.selectedPackageType,
      selectedProducts: state.selectedProducts,
      selectedAddOns: state.selectedAddOns,
      configureProducts: state.configureProducts,
      selectedPackageId: state.selectedPackageId,
      selectedClinic: state.selectedClinic,
      selectedDate,
      selectedTimeSlots,
      secondSlotCalendarDate: state.secondSlotCalendarDate,
      bookingType: state.bookingType,
      formData: state.formData,
      bookingId: state.bookingId,
      isLoading: state.isLoading,
      isSubmitting: state.isSubmitting,
      isSubmitted: state.isSubmitted,
      language: state.language,
      market: state.market,

      // ===== COMPUTED STATE =====
      firstAvailableDate,
      defaultSecondSlotDate,
      isDateValid,
      selectedProductDetails,
      selectedAddOnDetails,
      selectedClinicDetails,
      canProceed,
      canGoBack,

      // ===== ACTIONS =====
      ...actions,

      // Raw store access (for advanced usage)
      _store: useBookingFlowStore.getState(),
    }),
    [
      state,
      selectedDate,
      selectedTimeSlots,
      firstAvailableDate,
      defaultSecondSlotDate,
      isDateValid,
      selectedProductDetails,
      selectedAddOnDetails,
      selectedClinicDetails,
      canProceed,
      canGoBack,
      actions,
    ],
  );
};

/**
 * Hook for read-only access to specific parts of the booking flow
 * Useful for components that only need to display data
 */
export const useBookingFlowState = <T>(
  selector: (state: ReturnType<typeof useBookingFlowStore.getState>) => T,
): T => {
  return useBookingFlowStore(selector);
};
